import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BsArrowReturnRight } from "react-icons/bs";
import Input from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilterProps } from "./types";
type ComparisonType = "exact" | "less" | "greater" | "between";
const DEBOUNCE_MS = 500;
function inferInitialState(value: unknown): {
  type: ComparisonType;
  lower: string;
  upper: string;
} {
  if (!Array.isArray(value) || value.length !== 2) return {
    type: "exact",
    lower: "",
    upper: ""
  };
  const [a, b] = value as [number | null, number | null];
  if (a === null && b !== null) return {
    type: "less",
    lower: String(b),
    upper: ""
  };
  if (a !== null && b === null) return {
    type: "greater",
    lower: "",
    upper: String(a)
  };
  if (a !== null && b !== null && a === b) return {
    type: "exact",
    lower: String(a),
    upper: ""
  };
  if (a !== null && b !== null) return {
    type: "between",
    lower: String(a),
    upper: String(b)
  };
  return {
    type: "exact",
    lower: "",
    upper: ""
  };
}
const NumberRangeFilter: React.FC<FilterProps> = t0 => {
  const $ = _c(52);
  const {
    value,
    onChange,
    onSubmit
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = inferInitialState(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const init = t1;
  const [comparisonType, setComparisonType] = useState(init.type);
  const [lowerBound, setLowerBound] = useState(init.lower);
  const [upperBound, setUpperBound] = useState(init.upper);
  const [hasUserInput, setHasUserInput] = useState(false);
  const containerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const lastAppliedRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const focusInput = () => {
        const input = containerRef.current?.querySelector("input");
        if (input) {
          input.focus({
            preventScroll: true
          });
        }
      };
      requestAnimationFrame(focusInput);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== comparisonType) {
    t3 = [comparisonType];
    $[3] = comparisonType;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== onChange) {
    t6 = (type, lower, upper, userInput) => {
      if (!userInput && lower === "" && upper === "") {
        return;
      }
      let values;
      bb29: switch (type) {
        case "exact":
          {
            if (lower !== "") {
              const n_1 = parseInt(lower, 10);
              if (!isNaN(n_1)) {
                values = [n_1, n_1];
              }
            }
            break bb29;
          }
        case "less":
          {
            if (lower !== "") {
              const n_0 = parseInt(lower, 10);
              if (!isNaN(n_0)) {
                values = [null, n_0];
              }
            }
            break bb29;
          }
        case "greater":
          {
            if (upper !== "") {
              const n = parseInt(upper, 10);
              if (!isNaN(n)) {
                values = [n, null];
              }
            }
            break bb29;
          }
        case "between":
          {
            if (lower !== "" && upper !== "") {
              const l = parseInt(lower, 10);
              const u = parseInt(upper, 10);
              if (isNaN(l) || isNaN(u)) {
                break bb29;
              }
              values = [l, u];
            }
          }
      }
      const applyKey = values ? JSON.stringify(values) : "undefined";
      if (lastAppliedRef.current === applyKey) {
        return;
      }
      const allFieldsEmpty = type === "between" ? lower === "" && upper === "" : type === "greater" ? upper === "" : lower === "";
      if (values) {
        lastAppliedRef.current = applyKey;
        onChange(values);
      } else {
        if (userInput && allFieldsEmpty) {
          lastAppliedRef.current = applyKey;
          onChange(undefined);
        }
      }
    };
    $[7] = onChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const applyFilter = t6;
  let t7;
  if ($[9] !== applyFilter) {
    t7 = (type_0, lower_0, upper_0) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        applyFilter(type_0, lower_0, upper_0, true);
      }, DEBOUNCE_MS);
    };
    $[9] = applyFilter;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const debouncedApply = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = value_0 => {
      setComparisonType(value_0 as ComparisonType);
      setLowerBound("");
      setUpperBound("");
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleComparisonTypeChange = t8;
  let t9;
  if ($[12] !== comparisonType || $[13] !== debouncedApply || $[14] !== upperBound) {
    t9 = e => {
      const val = e.target.value;
      setLowerBound(val);
      setHasUserInput(true);
      debouncedApply(comparisonType, val, upperBound);
    };
    $[12] = comparisonType;
    $[13] = debouncedApply;
    $[14] = upperBound;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleLowerBoundChange = t9;
  let t10;
  if ($[16] !== comparisonType || $[17] !== debouncedApply || $[18] !== lowerBound) {
    t10 = e_0 => {
      const val_0 = e_0.target.value;
      setUpperBound(val_0);
      setHasUserInput(true);
      debouncedApply(comparisonType, lowerBound, val_0);
    };
    $[16] = comparisonType;
    $[17] = debouncedApply;
    $[18] = lowerBound;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleUpperBoundChange = t10;
  let t11;
  if ($[20] !== applyFilter || $[21] !== comparisonType || $[22] !== hasUserInput || $[23] !== lowerBound || $[24] !== upperBound) {
    t11 = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      applyFilter(comparisonType, lowerBound, upperBound, hasUserInput);
    };
    $[20] = applyFilter;
    $[21] = comparisonType;
    $[22] = hasUserInput;
    $[23] = lowerBound;
    $[24] = upperBound;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const handleBlur = t11;
  let t12;
  if ($[26] !== applyFilter || $[27] !== comparisonType || $[28] !== lowerBound || $[29] !== onSubmit || $[30] !== upperBound) {
    t12 = e_1 => {
      if (e_1.key === "Enter") {
        e_1.preventDefault();
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        applyFilter(comparisonType, lowerBound, upperBound, true);
        onSubmit?.();
      }
    };
    $[26] = applyFilter;
    $[27] = comparisonType;
    $[28] = lowerBound;
    $[29] = onSubmit;
    $[30] = upperBound;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const handleKeyDown = t12;
  let t13;
  if ($[32] !== comparisonType || $[33] !== handleBlur || $[34] !== handleKeyDown || $[35] !== handleLowerBoundChange || $[36] !== handleUpperBoundChange || $[37] !== lowerBound || $[38] !== upperBound) {
    t13 = () => {
      switch (comparisonType) {
        case "exact":
        case "less":
          {
            return <Input type="number" value={lowerBound} onChange={handleLowerBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" placeholder="Enter value" />;
          }
        case "greater":
          {
            return <Input type="number" value={upperBound} onChange={handleUpperBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" placeholder="Enter value" />;
          }
        case "between":
          {
            return <div className="flex w-full items-center gap-2"><Input type="number" value={lowerBound} onChange={handleLowerBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} autoFocus={true} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" placeholder="Enter value" /><span className="shrink-0 text-body text-muted-foreground">and</span><Input type="number" value={upperBound} onChange={handleUpperBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" placeholder="Enter value" /></div>;
          }
      }
    };
    $[32] = comparisonType;
    $[33] = handleBlur;
    $[34] = handleKeyDown;
    $[35] = handleLowerBoundChange;
    $[36] = handleUpperBoundChange;
    $[37] = lowerBound;
    $[38] = upperBound;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const renderInputs = t13;
  let t14;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SelectTrigger><SelectValue placeholder="Select comparison type" /></SelectTrigger>;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SelectContent><SelectItem value="exact">is equal to</SelectItem><SelectItem value="less">is less than or equal to</SelectItem><SelectItem value="greater">is greater than or equal to</SelectItem><SelectItem value="between">is between</SelectItem></SelectContent>;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== comparisonType) {
    t16 = <Select value={comparisonType} onValueChange={handleComparisonTypeChange}>{t14}{t15}</Select>;
    $[42] = comparisonType;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <BsArrowReturnRight className="ml-1.5 mr-1 h-5 w-4 flex-shrink-0 text-primary" />;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== renderInputs) {
    t18 = renderInputs();
    $[45] = renderInputs;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t18) {
    t19 = <div className="mt-1 flex items-center">{t17}<div className="mt-1 flex items-center">{t18}</div></div>;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t16 || $[50] !== t19) {
    t20 = <div ref={containerRef}>{t16}{t19}</div>;
    $[49] = t16;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  return t20;
};
export default NumberRangeFilter;